'use client'

/**
 * Global error boundary
 *
 * Catches unhandled React render errors across all routes.
 * Provides a friendly recovery path — retry button to recover.
 *
 * INVARIANTS:
 * ✅ No runtime ownership changes
 * ✅ No financial data exposed in error state
 * ✅ Consistent BPAL styling
 * ✅ Retry action resets error boundary + re-renders children
 * ✅ Does NOT catch errors in event handlers (those need try/catch)
 * ✅ Does NOT catch errors in Server Components (use error.tsx at segment)
 *
 * AUTHORITY: app/layout.tsx → Next.js App Router error.tsx convention
 */

import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white/40">
      <div className="flex flex-col items-center gap-6 max-w-md text-center px-6">
        {/* Icon */}
        <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200/50 flex items-center justify-center">
          <span className="text-amber-600 text-lg font-semibold">!</span>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <h1 className="text-lg font-semibold text-[#1a1818]">
          Something went wrong
          </h1>
          <p className="text-sm text-[#6b6762] leading-relaxed">
            An unexpected error occurred. Please try again.
            If the problem persists, contact support.
          </p>
        </div>

        {/* Error digest (for support reference) */}
        {error.digest && (
          <p className="text-[10px] text-[#b7aca2] font-mono">
            Reference: {error.digest}
          </p>
        )}

        {/* Retry */}
        <button
          onClick={reset}
          className="px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#1a1818] hover:bg-[#2d2b29] transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  )
}
